<template>
  <v-container fluid class="pa-0">
    <v-card :loading="loading" outlined class="order-list-card">
      <div class="order-list-header">
        <div class="order-title-wrap">
          <v-btn icon small class="mr-2" @click="$router.go(-1)">
            <v-icon>mdi-chevron-left</v-icon>
          </v-btn>

          <div>
            <div class="page-title">Order List</div>
            <div class="page-subtitle">
              {{ totalItems }} order{{ totalItems === 1 ? "" : "s" }} found
            </div>
          </div>
        </div>

        <div class="order-actions">
          <v-btn
            v-if="checkRightStatus(78)"
            small
            rounded
            color="primary"
            dark
            class="new-list-button"
            @click="add"
          >
            <v-icon left small>mdi-plus</v-icon>
            <span class="new-button-label">New</span>
          </v-btn>

          <v-btn
            v-if="hasRunningOrder"
            small
            rounded
            outlined
            @click="openRunning"
          >
            <v-icon left small>mdi-history</v-icon>
            Running Order
          </v-btn>
        </div>
      </div>

      <div class="order-filter-bar">
        <v-text-field
          v-model="search"
          outlined
          prepend-inner-icon="search"
          autofocus
          dense
          label="Search order, customer, total"
          single-line
          hide-details
          clearable
          class="order-search"
        />

        <template v-if="!$vuetify.breakpoint.xsOnly">
          <v-btn-toggle
            v-model="orderStatus"
            @change="getList()"
            dense
            mandatory
            class="status-toggle"
          >
            <v-btn value="all" small>All</v-btn>
            <v-btn value="draft" small>Draft</v-btn>
            <v-btn value="submitted" small>Submitted</v-btn>
            <v-btn value="completed" small>Completed</v-btn>
          </v-btn-toggle>

          <v-spacer />

          <DateTimePicker
            :defaultRange="defaultRange"
            :range="true"
            :hideTime="true"
            v-model="dateTime"
            @submit="getList()"
          />
        </template>

        <v-btn
          v-else
          icon
          class="filter-toolbar__mobile-trigger"
          @click="mobileFilterSheet = true"
        >
          <v-icon>mdi-tune-variant</v-icon>
        </v-btn>
      </div>

      <v-bottom-sheet
        v-show="$vuetify.breakpoint.smAndDown"
        v-model="mobileFilterSheet"
        inset
        max-width="520"
      >
        <v-card class="mobile-filter-card">
          <div class="mobile-filter-header">
            <v-btn icon @click="mobileFilterSheet = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>

            <div class="mobile-filter-title">Filters</div>
          </div>

          <v-card-text>
            <v-select
              v-model="orderStatus"
              :items="[
                { text: 'All', value: 'all' },
                { text: 'Draft', value: 'draft' },
                { text: 'Submitted', value: 'submitted' },
                { text: 'Completed', value: 'completed' },
              ]"
              item-text="text"
              item-value="value"
              label="Order status"
              outlined
              dense
              hide-details
              @change="getList()"
            />

            <v-divider class="my-4" />

            <DateTimePicker
              :defaultRange="defaultRange"
              :range="true"
              :hideTime="true"
              v-model="dateTime"
              @submit="getList()"
            />

            <v-select
              v-model="options.itemsPerPage"
              :items="[10, 25, 50, 100]"
              label="Rows per page"
              outlined
              dense
              hide-details
              class="mt-4"
              @change="options.page = 1"
            />

            <v-btn
              block
              color="primary"
              class="mt-4"
              rounded
              depressed
              @click="mobileFilterSheet = false"
            >
              Apply
            </v-btn>
          </v-card-text>
        </v-card>
      </v-bottom-sheet>

      <v-card-text class="pa-4">
        <div class="summary-row mb-4">
          <div class="summary-box">
            <div class="summary-label">List Total</div>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <div class="summary-value" v-bind="attrs" v-on="on">
                  {{ compactCurrency(total) }}
                </div>
              </template>
              <span>{{ fullCurrency(total) }}</span>
            </v-tooltip>
          </div>

          <div class="summary-box">
            <div class="summary-label">Result Total</div>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <div class="summary-value" v-bind="attrs" v-on="on">
                  {{ compactCurrency(fullTotal) }}
                </div>
              </template>
              <span>{{ fullCurrency(fullTotal) }}</span>
            </v-tooltip>
          </div>
        </div>

        <v-data-table
          :mobile-breakpoint="0"
          :headers="headers"
          :items="Invoices"
          :search="search"
          :loading="loading"
          :sort-by="sortBy"
          sort-desc
          :options.sync="options"
          :server-items-length="totalItems"
          :hide-default-footer="$vuetify.breakpoint.xsOnly"
          @current-items="updateTotal"
          :custom-filter="customSearch"
          :footer-props="{ 'items-per-page-options': [10, 15, 25, 50, 100] }"
          class="order-table"
        >
          <template v-slot:item="{ item }">
            <tr class="pointer order-row">
              <td class="text-left" @click="viewOrder(item)">
                {{ item.date | moment("MM/DD/YYYY hh:mm a") }}

                <div v-if="item.status === 'draft'" class="caption grey--text">
                  Draft started
                  {{ item.createdAt | moment("MM/DD/YYYY hh:mm a") }}
                </div>

                <div class="mt-1" v-if="hasSalesRepPriceSuggestions(item)">
                  <v-chip
                    small
                    color="deep-orange lighten-5"
                    text-color="deep-orange darken-3"
                    class="font-weight-bold"
                  >
                    <v-icon left x-small>mdi-tag-edit-outline</v-icon>

                    Price Changed

                    <span
                      v-if="
                        item.metadata &&
                        item.metadata.salesRepPriceSuggestionCount
                      "
                    >
                      ({{ item.metadata.salesRepPriceSuggestionCount }})
                    </span>
                  </v-chip>
                </div>
              </td>

              <td class="text-left" @click="viewOrder(item)">
                <span class="font-weight-bold">{{ item.localId }}</span>

                <div
                  v-if="
                    currentBusiness &&
                    currentBusiness.metadata &&
                    !currentBusiness.metadata.isSingleCustomerPricing &&
                    item.PriceTier
                  "
                  class="mt-1"
                >
                  <v-chip
                    small
                    class="font-weight-bold"
                    color="red"
                    text-color="white"
                  >
                    {{ item.PriceTier.name }}
                  </v-chip>
                </div>

                <div class="mt-1">
                  <v-chip
                    v-if="item.isRemoteOrder && !isRepairNestOrder(item)"
                    color="green lighten-5"
                    small
                    class="font-weight-bold mr-1"
                    text-color="green darken-3"
                  >
                    Online
                  </v-chip>

                  <v-chip
                    v-if="isRepairNestOrder(item)"
                    color="secondary"
                    dark
                    small
                    class="font-weight-bold mr-1"
                  >
                    <v-icon left x-small>mdi-storefront-outline</v-icon>
                    RepairNest
                  </v-chip>

                  <v-chip
                    v-if="item.isChocoOrder"
                    color="indigo lighten-5"
                    small
                    class="font-weight-bold"
                    text-color="indigo darken-3"
                  >
                    CHOCO
                  </v-chip>
                </div>
                <div
                  v-if="getRepairNestReference(item)"
                  class="customer-meta repairnest-reference mt-1"
                >
                  Ref: {{ getRepairNestReference(item) }}
                </div>
              </td>

              <td class="text-left" @click="viewOrder(item)">
                <v-chip
                  v-if="item.source === 'catalog'"
                  small
                  color="blue lighten-5"
                  text-color="blue darken-3"
                  class="font-weight-bold"
                >
                  Catalog
                </v-chip>

                <v-chip
                  v-else-if="item.source === 'repairnest'"
                  small
                  color="secondary"
                  dark
                  class="font-weight-bold"
                >
                  RepairNest
                </v-chip>

                <v-chip
                  v-else-if="item.source === 'remote'"
                  small
                  color="green lighten-5"
                  text-color="green darken-3"
                  class="font-weight-bold"
                >
                  Remote
                </v-chip>

                <v-chip
                  v-else-if="item.source === 'inHouse'"
                  small
                  color="pink lighten-5"
                  text-color="pink darken-3"
                  class="font-weight-bold"
                >
                  In House
                </v-chip>

                <v-chip
                  v-else-if="item.source === 'choco'"
                  small
                  color="indigo lighten-5"
                  text-color="indigo darken-3"
                  class="font-weight-bold"
                >
                  Choco
                </v-chip>

                <span v-else> -- </span>
              </td>

              <td v-if="!CustomerId" class="text-left" @click="viewOrder(item)">
                <div v-if="isRepairNestOrder(item) && item.retailer">
                  <div class="font-weight-bold">{{ item.retailer.businessName || item.retailer.name || item.retailer.locationName }}</div>
                  <div v-if="item.retailer.locationName && item.retailer.locationName !== item.retailer.businessName" class="customer-meta">Location: {{ item.retailer.locationName }}</div>
                </div>

                <div v-else-if="item.Customer">
                  <div class="font-weight-bold">
                    {{ item.Customer.name }}
                  </div>
                </div>

                <div v-else-if="getPublicCustomer(item)">
                  <div class="font-weight-bold">
                    {{
                      getPublicCustomer(item).businessName ||
                      getPublicCustomer(item).name
                    }}
                  </div>

                  <div
                    v-if="
                      getPublicCustomer(item).name &&
                      getPublicCustomer(item).businessName &&
                      getPublicCustomer(item).name !==
                        getPublicCustomer(item).businessName
                    "
                    class="customer-meta"
                  >
                    Contact: {{ getPublicCustomer(item).name }}
                  </div>

                  <div
                    v-if="getPublicCustomer(item).phoneNumber"
                    class="customer-meta"
                  >
                    Phone: {{ getPublicCustomer(item).phoneNumber }}
                  </div>

                  <div
                    v-if="getPublicCustomer(item).email"
                    class="customer-meta"
                  >
                    Email: {{ getPublicCustomer(item).email }}
                  </div>

                  <v-chip
                    small
                    class="mt-1"
                    color="orange lighten-5"
                    text-color="orange darken-3"
                  >
                    Unknown Customer
                  </v-chip>
                </div>

                <div v-else>
                  Unknown Customer
                  <v-chip small class="ml-1">Unknown</v-chip>
                </div>
              </td>

              <td class="text-right font-weight-bold" @click="viewOrder(item)">
                {{ item.total | currency }}
              </td>

              <td class="text-left">
                <v-chip
                  v-if="item.status === 'draft'"
                  color="blue lighten-5"
                  small
                  text-color="blue darken-3"
                  class="font-weight-bold"
                >
                  Draft
                </v-chip>

                <v-chip
                  v-else-if="item.status === 'completed' || item.completeDate"
                  color="green lighten-5"
                  small
                  text-color="green darken-3"
                  class="font-weight-bold"
                >
                  Completed
                </v-chip>

                <v-chip
                  v-else
                  color="orange lighten-5"
                  small
                  text-color="orange darken-3"
                  class="font-weight-bold"
                >
                  Submitted
                </v-chip>
              </td>

              <td class="text-left" @click="viewOrder(item)">
                <span v-if="item.completeDate">
                  {{ item.completeDate | moment("MM/DD/YYYY hh:mm a") }}
                </span>
                <span v-else>--</span>
              </td>

              <td class="justify-end align-center text-right">
                <v-menu bottom left offset-y>
                  <template v-slot:activator="{ on }">
                    <v-btn v-on="on" icon small>
                      <v-icon>mdi-dots-vertical</v-icon>
                    </v-btn>
                  </template>

                  <v-list dense class="menu-button">
                    <v-list-item
                      v-if="!CustomerId && item.CustomerId"
                      @click="viewProfile(item)"
                    >
                      <v-list-item-title>Profile</v-list-item-title>
                    </v-list-item>

                    <v-divider v-if="!CustomerId && item.CustomerId" />

                    <v-list-item @click="viewOrder(item)">
                      <v-list-item-title>View Order</v-list-item-title>
                    </v-list-item>

                    <v-divider />

                    <v-list-item
                      v-if="item.status !== 'completed' && !item.completeDate"
                      @click="editOrder(item)"
                    >
                      <v-list-item-title>Edit</v-list-item-title>
                    </v-list-item>

                    <v-list-item @click="deleteOrder(item)">
                      <v-list-item-title class="red--text">
                        Delete
                      </v-list-item-title>
                    </v-list-item>

                    <v-divider
                      v-if="item.status === 'submitted' && !item.completeDate"
                    />

                    <v-list-item
                      v-if="item.status === 'submitted' && !item.completeDate"
                      :disabled="item.Customer && item.Customer.blockOrder"
                      @click="convertToInvoice(item)"
                    >
                      <v-list-item-title>
                        <div>Convert to invoice</div>

                        <div
                          v-if="item.Customer && item.Customer.blockOrder"
                          class="mt-1"
                        >
                          <v-chip small color="red" text-color="white">
                            Blocked
                          </v-chip>
                        </div>
                      </v-list-item-title>
                    </v-list-item>

                    <v-divider />

                    <v-list-item @click="openHistory(item)">
                      <v-list-item-title>View History</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </td>
            </tr>
          </template>

          <v-alert slot="no-results" :value="true" color="error" icon="warning">
            Your search for "{{ search }}" found no results.
          </v-alert>

        </v-data-table>

        <v-pagination
          v-if="$vuetify.breakpoint.xsOnly && mobilePageCount > 1"
          v-model="options.page"
          :length="mobilePageCount"
          :total-visible="5"
          circle
          class="mobile-pagination mt-3"
        />
      </v-card-text>
    </v-card>

    <OrderHistoryDialog
      v-show="historyDialog"
      v-model="historyDialog"
      :order="historyOrder"
    />
  </v-container>
</template>

<script>
import orderService from "./InHouse/service";
import OrderHistoryDialog from "@/modules/Order/components/OrderHistoryDialog.vue";
import { mapGetters } from "vuex";
import Hashids from "hashids";

const DateTimePicker = () => import("@/components/DateTimePicker");
const hashids = new Hashids();

export default {
  name: "order-list",

  props: {
    CustomerId: {
      default: null,
    },
    showAll: {
      default: false,
    },
    due: {
      default: false,
    },
  },

  components: {
    DateTimePicker,
    OrderHistoryDialog,
  },

  data() {
    return {
      historyDialog: false,
      historyOrder: null,
      totalItems: 0,
      mobileFilterSheet: false,
      hasRunningOrder: false,
      defaultRange: "since90DaysAgo",
      orderStatus: "all",
      fullTotal: 0,
      total: 0,
      dateTime: {
        start: this.$moment()
          .subtract(90, "days")
          .startOf("day")
          .format("YYYY-MM-DD"),
        end: this.$moment().endOf("day").format("YYYY-MM-DD"),
      },
      count: null,
      headers: [],
      Invoices: [],
      search: null,
      loading: true,
      options: {
        page: 1,
        itemsPerPage: 10,
        sortBy: [],
        sortDesc: [],
      },
      sortBy: "localId",
      pageCount: 0,
      itemsPerPage: 10,
    };
  },

  computed: {
    ...mapGetters("global", [
      "currentUser",
      "checkRightStatus",
      "currentBusiness",
    ]),
    mobilePageCount() {
      return Math.max(
        1,
        Math.ceil(this.totalItems / (this.options.itemsPerPage || 10))
      );
    },
  },

  watch: {
    CustomerId: {
      immediate: true,
      handler() {
        this.getList();
      },
    },

    options: {
      deep: true,
      handler() {
        this.getList();
      },
    },
  },

  created() {
    this.checkRunningOrder();
    this.loadHeader();
    document.title = "smplsale - Order List";
  },

  methods: {
    compactCurrency(value) {
      const amount = Number(value || 0);
      const absolute = Math.abs(amount);
      let divisor = 1;
      let suffix = "";

      if (absolute >= 1000000) {
        divisor = 1000000;
        suffix = "M";
      } else if (absolute >= 1000) {
        divisor = 1000;
        suffix = "K";
      }

      const compact = amount / divisor;
      const digits = suffix ? 1 : 2;
      return `$${compact.toLocaleString("en-US", {
        minimumFractionDigits: 0,
        maximumFractionDigits: digits,
      })}${suffix}`;
    },
    fullCurrency(value) {
      return Number(value || 0).toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
      });
    },
    isRepairNestOrder(item) {
      return !!(item && (item.source === "repairnest" || item.sourceLabel === "RepairNest" || item.displayOrderNumber || (item.metadata && item.metadata.source === "repairnest")));
    },
    getRepairNestReference(item) {
      if (!this.isRepairNestOrder(item)) return null;
      return item.displayOrderNumber || item.marketOrderNumber || null;
    },
    hasSalesRepPriceSuggestions(item) {
      return !!(
        item &&
        item.metadata &&
        item.metadata.hasSalesRepPriceSuggestions
      );
    },
    getPublicCustomer(item) {
      if (!item || !item.metadata) return null;

      if (item.metadata.publicCustomer) {
        return item.metadata.publicCustomer;
      }

      if (
        item.metadata.name ||
        item.metadata.businessName ||
        item.metadata.phoneNumber ||
        item.metadata.email
      ) {
        return {
          name: item.metadata.name,
          businessName: item.metadata.businessName,
          phoneNumber: item.metadata.phoneNumber,
          email: item.metadata.email,
        };
      }

      return null;
    },
    checkRunningOrder() {
      const draft = localStorage.getItem("runningOrder");

      if (!draft) {
        this.hasRunningOrder = false;
        return;
      }

      try {
        const parsed = JSON.parse(draft);
        this.hasRunningOrder = !!(
          parsed &&
          parsed.Order &&
          parsed.Order.OrderDetails &&
          parsed.Order.OrderDetails.length > 0
        );
      } catch (e) {
        localStorage.removeItem("runningOrder");
        this.hasRunningOrder = false;
      }
    },

    customSearch(value, search, item) {
      if (!search) {
        return true;
      }

      const term = search.toString().toLowerCase();

      const customerName =
        item.Customer && item.Customer.name
          ? item.Customer.name.toLowerCase()
          : "";

      const publicCustomer = this.getPublicCustomer(item);

      const metadataName =
        publicCustomer && publicCustomer.name
          ? publicCustomer.name.toLowerCase()
          : "";

      const metadataBusinessName =
        publicCustomer && publicCustomer.businessName
          ? publicCustomer.businessName.toLowerCase()
          : "";

      const metadataPhone =
        publicCustomer && publicCustomer.phoneNumber
          ? publicCustomer.phoneNumber.toLowerCase()
          : "";

      const metadataEmail =
        publicCustomer && publicCustomer.email
          ? publicCustomer.email.toLowerCase()
          : "";

      const localId = item.localId ? item.localId.toString().toLowerCase() : "";
      const total = item.total ? item.total.toString().toLowerCase() : "";
      const repairNestReference = this.getRepairNestReference(item);
      const normalizedRepairNestReference = repairNestReference
        ? repairNestReference.toString().toLowerCase()
        : "";

      return (
        customerName.includes(term) ||
        metadataName.includes(term) ||
        metadataBusinessName.includes(term) ||
        metadataPhone.includes(term) ||
        metadataEmail.includes(term) ||
        normalizedRepairNestReference.includes(term) ||
        localId.includes(term) ||
        total.includes(term)
      );
    },

    convertToInvoice(order) {
      this.$swal({
        title: "Are you sure?",
        text: "You want to convert this order to invoice?",
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        cancelButtonText: "No",
        confirmButtonText: "Yes",
      }).then((result) => {
        if (result.value) {
          orderService.getById(order.id).then((obj) => {
            this.$router.push({
              name: "invoice-create",
              params: {
                passData: obj.data,
                type: "order",
              },
            });
          });
        }
      });
    },

    updateTotal(val) {
      this.total = val.reduce((sum, row) => {
        return sum + Number(row.total || 0);
      }, 0);
    },

    openHistory(order) {
      if (!this.currentBusiness.metadata.historyTracking) {
        this.$swal({
          icon: "warning",
          title: "History Tracking Off",
          text: "Enable it in Settings to view change logs ($50/month).",
        });
        return;
      }

      this.historyOrder = order;
      this.historyDialog = true;
    },

    openRunning() {
      this.resumeOrder();
    },

    add() {
      if (this.hasRunningOrder) {
        this.$swal({
          title: "Are you sure?",
          text: "There is a running order. Do you want to clear it?",
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
          cancelButtonText: "No",
          confirmButtonText: "Yes",
        }).then((result) => {
          if (result.value) {
            localStorage.removeItem("runningOrder");
            this.hasRunningOrder = false;
            this.goToCreateOrder();
          }
        });

        return;
      }

      this.goToCreateOrder();
    },

    resumeOrder() {
      const draft = localStorage.getItem("runningOrder");

      if (!draft) {
        this.$swal({
          icon: "info",
          title: "No running order found",
          text: "It looks like there's no saved order to resume.",
          confirmButtonText: "Okay",
        });
        return;
      }

      try {
        const parsed = JSON.parse(draft);

        if (parsed && parsed.Order && parsed.Order.CustomerId) {
          this.$router.push({
            path: `/order/create?CustomerId=${parsed.Order.CustomerId}`,
          });
        } else {
          this.$router.push({ path: "/order/create" });
        }
      } catch (e) {
        localStorage.removeItem("runningOrder");

        this.$swal({
          icon: "error",
          title: "Something went wrong",
          text: "Your saved order couldn't be resumed.",
          confirmButtonText: "Okay",
        });
      }
    },

    goToCreateOrder() {
      if (this.CustomerId) {
        this.$router.push({
          path: `/order/create?CustomerId=${this.CustomerId}`,
        });
      } else {
        this.$router.push({ path: "/order/create" });
      }
    },

    viewProfile(val) {
      if (!val.CustomerId) {
        return;
      }

      this.$router.push({ path: `/customer/${val.CustomerId}/profile` });
    },

    viewOrder(order) {
      const hashId = hashids.encode(order.id);

      const routeData = this.$router.resolve({
        path: `/global/order/${hashId}/view`,
      });

      window.open(routeData.href, "newwindow", "width=850, height=1100");
    },

    loadHeader() {
      this.headers = [
        {
          text: "Date",
          align: "left",
          value: "date",
          sortable: true,
        },
        {
          text: "Order Number",
          align: "left",
          value: "localId",
          sortable: true,
        },
        {
          text: "Source",
          align: "left",
          value: "source",
          sortable: true,
        },
      ];

      if (!this.CustomerId) {
        this.headers.push({
          text: "Customer Name",
          value: "Customer.name",
          align: "left",
          sortable: true,
        });
      }

      this.headers.push(
        {
          text: "Total",
          value: "total",
          align: "right",
          sortable: true,
        },
        {
          text: "Status",
          value: "status",
          align: "left",
          sortable: false,
        },
        {
          text: "Complete Date",
          align: "left",
          value: "completeDate",
          sortable: true,
        },
        {
          text: "",
          value: "action",
          sortable: false,
        }
      );
    },

    getList() {
      this.loading = true;

      const where = {};

      if (!this.showAll) {
        where.start = this.dateTime.start;
        where.end = this.dateTime.end;
      }

      if (this.CustomerId) {
        where.CustomerId = this.CustomerId;
      }

      if (!this.currentUser.isAdmin && this.checkRightStatus(50)) {
        where.assingedCustomer = this.currentUser.id;
      }

      // if (this.orderStatus === "pending") {
      //   where.pending = true;
      // }

      // if (this.orderStatus === "completed") {
      //   where.completed = true;
      // }

      if (this.orderStatus === "all") {
        where.includeDraft = true;
      }

      if (this.orderStatus && this.orderStatus !== "all") {
        where.status = this.orderStatus;
      }

      const itemsPerPage = this.options.itemsPerPage || 10;
      const page = this.options.page || 1;

      where.limit = itemsPerPage;
      where.offset = (page - 1) * itemsPerPage;

      if (this.options.sortBy && this.options.sortBy.length > 0) {
        where.sortBy = this.options.sortBy[0];
        where.sortDesc =
          this.options.sortDesc && this.options.sortDesc.length > 0
            ? this.options.sortDesc[0]
            : false;
      }

      return orderService
        .getAll(where)
        .then((response) => {
          this.Invoices = response.data || [];
          this.totalItems = Number(response.count || 0);
          this.fullTotal = Number(
            response.totals && response.totals.total ? response.totals.total : 0
          );
          this.count = response.count;
          return response;
        })
        .catch((err) => {
          console.error("Order list fetch failed", err);
          this.Invoices = [];
          this.totalItems = 0;
          this.fullTotal = 0;
        })
        .finally(() => {
          this.loading = false;
        });
    },

    editOrder(order) {
      this.$router.push({ path: `/order/${order.id}/edit` });
    },

    deleteOrder(order) {
      if (
        this.currentBusiness.metadata &&
        this.currentBusiness.metadata.deletePrecaution
      ) {
        this.$swal
          .fire({
            title: "Are you sure?",
            html: `You won't be able to revert this.</br> <div style="color:red;">Type <span style="font-weight: bold">delete</span> in order remove this</div>`,
            icon: "question",
            input: "text",
            inputAttributes: {
              autocapitalize: "off",
            },
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: "Delete",
          })
          .then((result) => {
            if (result.value && result.value === "delete") {
              orderService.delete(order.id).then((response) => {
                if (response.data) {
                  this.$swal("Deleted!", "Order has been deleted.", "success");
                  this.getList();
                }
              });
            } else if (result.dismiss !== "cancel") {
              this.$swal("Delete", 'Type "delete" to delete item', "error");
            }
          });
      } else {
        this.$swal({
          title: "Are you sure?",
          text: "You won't be able to revert this!",
          icon: "question",
          showCancelButton: true,
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
          cancelButtonText: "No",
          confirmButtonText: "Yes",
        }).then((result) => {
          if (result.value) {
            orderService.delete(order.id).then((response) => {
              if (response.data) {
                this.$swal("Deleted!", "Order has been deleted.", "success");
                this.getList();
              }
            });
          }
        });
      }
    },
  },
};
</script>

<style scoped>
.order-list-page {
  background: #f8fafc;
}

.order-list-card {
  border-radius: 20px;
  background: #ffffff;
}

.order-list-header {
  padding: 16px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}

.order-title-wrap {
  display: flex;
  align-items: center;
}

.page-title {
  font-size: 22px;
  font-weight: 800;
  color: #0f172a;
}

.page-subtitle {
  font-size: 13px;
  color: #64748b;
  margin-top: 2px;
}

.order-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.order-filter-bar {
  padding: 12px 16px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.order-search {
  max-width: 340px;
  min-width: 260px;
}

.status-toggle {
  background: #f8fafc;
  border-radius: 14px;
  padding: 2px;
}

.summary-row {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 220px));
  gap: 12px;
}

.summary-box {
  background: #f8fafc;
  border-radius: 16px;
  padding: 14px;
}

.summary-label {
  font-size: 12px;
  color: #64748b;
}

.summary-value {
  font-size: 22px;
  font-weight: 800;
  color: #0f172a;
  margin-top: 4px;
}

.order-table {
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  overflow: hidden;
}

.order-row:hover {
  background: #f8fafc;
}

.mobile-filter-card {
  border-radius: 20px 20px 0 0;
}

.mobile-filter-header {
  padding: 10px 12px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.mobile-filter-title {
  font-size: 16px;
  font-weight: 800;
  color: #0f172a;
}

.customer-meta {
  font-size: 12px;
  color: #64748b;
  line-height: 1.35;
}

.repairnest-reference {
  overflow-wrap: anywhere;
}

@media (max-width: 600px) {
  .order-list-header {
    flex-wrap: nowrap;
  }

  .order-search {
    max-width: none;
    min-width: 0;
    flex: 1;
  }

  .summary-row {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
  }

  .order-actions {
    width: auto;
    margin-left: auto;
  }

  .order-actions .new-list-button {
    width: 40px;
    min-width: 40px;
    height: 40px;
    padding: 0;
    border-radius: 50%;
    align-items: center;
    justify-content: center;
  }

  .new-list-button .v-icon {
    margin: 0 !important;
  }

  .new-button-label {
    display: none;
  }

  .summary-box {
    padding: 12px;
  }

  .summary-value {
    font-size: 20px;
  }

  .mobile-pagination {
    display: flex;
    justify-content: center;
  }

  .mobile-pagination::v-deep .v-pagination__item,
  .mobile-pagination::v-deep .v-pagination__navigation {
    background: #ffffff !important;
    border: 1px solid #cbd5e1;
    box-shadow: none;
  }

  .mobile-pagination::v-deep .v-pagination__item--active {
    color: var(--v-primary-base) !important;
    border-color: var(--v-primary-base);
    font-weight: 700;
  }
}
</style>
